import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../App.jsx';
import api from '../services/api.js';
import { APP_VERSION } from '../version.js';
import NavBar from '../components/NavBar.jsx';

const THEME_COLORS = {
  espace: { leaf: '#4A90D9', glow: 'rgba(74,144,217,0.4)', icon: '🚀' },        // blue
  geographie: { leaf: '#2e7d32', glow: 'rgba(46,125,50,0.4)', icon: '🌍' },      // green
  mythologie: { leaf: '#9c27b0', glow: 'rgba(156,39,176,0.4)', icon: '⚡' },      // purple
  'corps-humain': { leaf: '#e53935', glow: 'rgba(229,57,53,0.4)', icon: '🫀' },   // red
  histoire: { leaf: '#F5A623', glow: 'rgba(245,166,35,0.4)', icon: '⏳' },        // amber
  animaux: { leaf: '#00ACC1', glow: 'rgba(0,172,193,0.4)', icon: '🦁' },          // cyan/teal
  personnages: { leaf: '#E91E90', glow: 'rgba(233,30,144,0.4)', icon: '👤' },     // pink/magenta
  sport: { leaf: '#FF6B35', glow: 'rgba(255,107,53,0.4)', icon: '⚽' },           // orange
  inventions: { leaf: '#FFD93D', glow: 'rgba(255,217,61,0.4)', icon: '💡' },      // yellow
  saveurs: { leaf: '#E8553A', glow: 'rgba(232,85,58,0.4)', icon: '🌶️' },         // chili red
};

// ── Paliers de maturité affichés sous l'arbre (juste pour l'étiquette,
// le rendu visuel se base sur le nombre exact de cartes débloquées). ──
function getMaturityStage(unlocked, total) {
  if (total === 0) return { id: 'dormant', label: 'Endormi', emoji: '💤' };
  const pct = unlocked / total;
  if (unlocked === 0) return { id: 'seed', label: 'Graine', emoji: '🌱' };
  if (unlocked < 5) return { id: 'germe', label: 'Germe', emoji: '🌱' };
  if (unlocked < 15) return { id: 'sprout', label: 'Pousse', emoji: '🌿' };
  if (unlocked < 35) return { id: 'young_sapling', label: 'Jeune arbuste', emoji: '🌿' };
  if (pct < 0.25) return { id: 'sapling', label: 'Arbuste', emoji: '🌿' };
  if (pct < 0.50) return { id: 'young', label: 'Jeune arbre', emoji: '🌳' };
  if (pct < 0.75) return { id: 'adult', label: 'Arbre adulte', emoji: '🌳' };
  if (pct < 1.0) return { id: 'mature', label: 'Arbre mature', emoji: '✨🌳' };
  return { id: 'legendary', label: 'Arbre légendaire', emoji: '👑' };
}

const LEVEL_EMOJIS = {
  1: '🌱', 2: '🌿', 3: '🪴', 4: '🌳', 5: '🌲',
  6: '🎄', 7: '🏔️', 8: '👑', 9: '💎', 10: '⭐',
};

function analyzeTree(tree) {
  if (!tree || tree.total_leaves === 0) return null;

  const messages = [];
  const branches = tree.branches.filter(b => b.total_cards > 0);
  if (branches.length === 0) return null;

  // Calculate percentages per theme
  const themed = branches.map(b => ({
    name: b.theme_name.split(' ')[0],
    slug: b.theme_slug,
    pct: Math.round(b.unlocked_cards / b.total_cards * 100),
    cards: b.unlocked_cards,
    total: b.total_cards,
    mastered: b.mastered_cards,
    accuracy: b.accuracy,
    icon: (THEME_COLORS[b.theme_slug] || {}).icon || '📖',
  }));

  const best = [...themed].sort((a, b) => b.pct - a.pct)[0];
  const worst = [...themed].sort((a, b) => a.pct - b.pct)[0];
  const avgPct = Math.round(themed.reduce((s, t) => s + t.pct, 0) / themed.length);
  const spread = best.pct - worst.pct;
  const activeBranches = themed.filter(t => t.cards > 0).length;
  const untouched = themed.filter(t => t.cards === 0);

  // Strong theme
  if (best.pct >= 40) {
    messages.push({
      icon: best.icon,
      text: `${best.name} est ta branche la plus forte ! ${best.pct}% exploré.`,
      type: 'positive',
    });
  }

  // Weak / untouched theme
  if (untouched.length > 0) {
    const u = untouched[0];
    messages.push({
      icon: '🌱',
      text: `Ta branche ${u.name} n'a pas encore poussé. Lance un quiz pour la faire grandir !`,
      type: 'encourage',
    });
  } else if (spread > 30) {
    messages.push({
      icon: worst.icon,
      text: `Ta branche ${worst.name} a besoin d'attention (${worst.pct}%). Un petit quiz ?`,
      type: 'encourage',
    });
  }

  // Balance check
  if (activeBranches >= 3 && spread <= 15) {
    messages.push({
      icon: '⚖️',
      text: 'Bravo, ton savoir est bien équilibré entre les thèmes !',
      type: 'positive',
    });
  }

  // Accuracy insight
  const bestAccuracy = [...themed].filter(t => t.cards > 0).sort((a, b) => b.accuracy - a.accuracy)[0];
  if (bestAccuracy && bestAccuracy.accuracy >= 80) {
    messages.push({
      icon: '🎯',
      text: `Tu maîtrises ${bestAccuracy.name} à ${bestAccuracy.accuracy}% de précision !`,
      type: 'positive',
    });
  }

  // Mastery progress
  const totalMastered = tree.total_mastered;
  const totalLeaves = tree.total_leaves;
  if (totalLeaves >= 5) {
    const masteryPct = Math.round(totalMastered / totalLeaves * 100);
    if (masteryPct >= 90) {
      messages.push({ icon: '👑', text: 'Presque toutes tes feuilles sont dorées. Impressionnant !', type: 'positive' });
    } else if (masteryPct < 50) {
      messages.push({ icon: '💪', text: `${totalMastered}/${totalLeaves} feuilles maîtrisées. Rejoue les thèmes pour les faire briller !`, type: 'encourage' });
    }
  }

  // Overall progress
  const overallPct = Math.round(tree.total_leaves / tree.total_possible * 100);
  if (overallPct >= 50) {
    messages.push({ icon: '🌳', text: `Ton arbre est déjà bien touffu avec ${overallPct}% des feuilles !`, type: 'positive' });
  }

  // Level milestone
  if (tree.level >= 7) {
    messages.push({ icon: '✨', text: 'Ton Curiosaurus est devenu majestueux. Continue comme ça !', type: 'positive' });
  } else if (tree.level <= 2 && tree.total_leaves > 0) {
    messages.push({ icon: '🌿', text: 'Ta pousse grandit bien ! Encore quelques quiz et elle deviendra un arbre.', type: 'encourage' });
  }

  // Limit to 3 messages: prioritize variety
  const positives = messages.filter(m => m.type === 'positive');
  const encourages = messages.filter(m => m.type === 'encourage');
  const result = [];
  if (encourages.length > 0) result.push(encourages[0]);
  for (const p of positives) { if (result.length < 3) result.push(p); }
  if (result.length < 3 && encourages.length > 1) result.push(encourages[1]);

  return result.slice(0, 3);
}

function generateLeafPositions(branchIndex, totalBranches, leafCount, maxLeaves, trunkTopY) {
  const leaves = [];
  const centerX = 500;
  // trunkTopY is passed in from the actual trunk position

  // Fixed angles for known branch counts — hand-tuned for good visual spread
  // Angles in degrees, measured from right (0°=right, -90°=up, -180°=left)
  const FIXED_ANGLES = {
    1: [-90],
    2: [-120, -60],
    3: [-135, -90, -45],
    4: [-150, -115, -65, -30],
    5: [-155, -120, -90, -60, -25],
    6: [-160, -128, -96, -64, -32, -20],
    7: [-162, -135, -108, -81, -54, -27, -18],
    8: [-165, -140, -117, -95, -73, -50, -28, -15],
    9: [-168, -147, -126, -105, -84, -63, -42, -21, -12],
  };

  const angles = FIXED_ANGLES[totalBranches] || FIXED_ANGLES[9];
  const branchAngleDeg = angles[Math.min(branchIndex, angles.length - 1)];
  const radAngle = (branchAngleDeg * Math.PI) / 180;

  const branchProgress = Math.min(1, leafCount / Math.max(maxLeaves, 1));
  // Branch length: longer branches spread leaves better
  const baseBranchLen = totalBranches > 6 ? 100 : totalBranches > 4 ? 120 : 140;
  const branchLen = baseBranchLen + branchProgress * 60;
  
  const branchEndX = centerX + Math.cos(radAngle) * branchLen;
  const branchEndY = trunkTopY + Math.sin(radAngle) * branchLen;

  // Seeded random for stable organic positions
  let seed = branchIndex * 7919 + 137;
  const rand = () => { seed = (seed * 16807 + 0) % 2147483647; return (seed - 1) / 2147483646; };

  // For many leaves, spread them in a cluster at the branch end
  const clusterRadius = Math.min(40, 15 + leafCount * 1.2);

  for (let i = 0; i < leafCount; i++) {
    // Place leaves along the branch (20% to 100%) with clustering near the tip
    const rawT = (i + 0.5) / Math.max(leafCount, 1);
    const t = 0.25 + Math.pow(rawT, 0.6) * 0.75;

    const baseX = centerX + Math.cos(radAngle) * branchLen * t;
    const baseY = trunkTopY + Math.sin(radAngle) * branchLen * t;

    // Perpendicular scatter (like foliage around the branch)
    const perpAngle = radAngle + Math.PI / 2;
    const perpSpread = clusterRadius * (0.3 + t * 0.7); // more spread near tip
    const scatterPerp = (rand() - 0.5) * 2 * perpSpread;
    const scatterAlong = (rand() - 0.5) * 16;

    const leafX = baseX + Math.cos(perpAngle) * scatterPerp + Math.cos(radAngle) * scatterAlong;
    const leafY = baseY + Math.sin(perpAngle) * scatterPerp + Math.sin(radAngle) * scatterAlong;

    leaves.push({
      x: Math.max(25, Math.min(975, leafX)),
      y: Math.max(25, Math.min(620, leafY)),
      windDelay: rand() * 4,
      windAmp: 1 + rand() * 2,
    });
  }
  return { branchEndX, branchEndY, branchAngle: radAngle, branchLen, leaves };
}

// ── Génération de feuilles pour un arbre mono-thème ──
// Disposition organique stable autour du sommet du tronc : on génère
// TOUJOURS 200 positions (séparées en 4 anneaux concentriques pour absorber
// le volume), et on n'en affiche que les `visibleCount` premières. Comme ça
// quand l'enfant débloque une carte, une nouvelle feuille apparaît sans
// que les autres bougent.
// Le seed dépend du thème pour que chaque arbre ait sa propre disposition.
// Les 6 mêmes angles que les branches dessinées dans le SVG (en degrés, repère SVG).
// DOIT rester synchronisé avec le bloc de rendu des branches en éventail.
const MONO_BRANCH_ANGLES = [-155, -125, -105, -90, -75, -55, -25];
// Ordre d'APPARITION des branches au fil de la croissance. La 1re branche part
// SUR LE CÔTÉ (-105°, pas la verticale) pour bien se distinguer du tronc, puis
// la 2e en symétrique (-75°), puis le centre (-90°) se remplit, puis l'éventail
// s'ouvre vers l'extérieur. Indices dans MONO_BRANCH_ANGLES.
// L'arbre passe ainsi de 1 → 7 branches au lieu d'avoir toujours l'éventail complet.
const MONO_BRANCH_GROWTH_ORDER = [2, 4, 3, 1, 5, 0, 6];
// Longueur de branche utilisée pour ancrer les feuilles. On prend la longueur
// MAXIMALE que la branche peut atteindre (140 + 70) pour que, quel que soit le
// nombre de feuilles, elles restent posées sur la portion dessinée de la branche.
const MONO_BRANCH_LEN = 140 + 70;

// ── Génération de feuilles pour un arbre mono-thème ──
// Les feuilles sont réparties LE LONG des 6 branches dessinées (pas dans des
// anneaux flottants), avec une dispersion perpendiculaire pour faire du feuillage
// autour de la branche. Chaque slot est stable : débloquer une carte ajoute une
// feuille sans déplacer les autres. On génère 200 slots (répartis en tourniquet
// sur les 6 branches), mélangés de façon déterministe pour une pousse harmonieuse.
function generateMonoTreeLeaves(visibleCount, trunkTopY, themeSeed = 0, branchAngles = MONO_BRANCH_ANGLES) {
  const TOTAL_SLOTS = 200;
  const centerX = 500;
  const leaves = [];

  if (visibleCount === 0 || branchAngles.length === 0) return leaves;

  // PRNG seedé pour stabilité — chaque thème a son seed distinct
  let seed = 31337 + themeSeed * 7919;
  const rand = () => { seed = (seed * 16807 + 13) % 2147483647; return (seed - 1) / 2147483646; };

  const nBranches = branchAngles.length;
  // Répartition en tourniquet : on remplit branche 0, 1, 2... puis on recommence.
  // Le rang sur la branche détermine la position le long de celle-ci.
  const perBranchCount = Math.ceil(TOTAL_SLOTS / nBranches);

  let slotIndex = 0;
  for (let rank = 0; rank < perBranchCount; rank++) {
    for (let b = 0; b < nBranches; b++) {
      if (slotIndex >= TOTAL_SLOTS) break;
      const rad = (branchAngles[b] * Math.PI) / 180;

      // Position le long de la branche : de 30% (près du tronc) à 100% (bout),
      // avec densité plus forte vers l'extérieur. rank croissant = plus haut.
      const rawT = (rank + 0.5) / perBranchCount;
      const t = 0.30 + Math.pow(rawT, 0.7) * 0.70;

      const baseX = centerX + Math.cos(rad) * MONO_BRANCH_LEN * t;
      const baseY = trunkTopY + Math.sin(rad) * MONO_BRANCH_LEN * t;

      // Dispersion perpendiculaire à la branche (feuillage autour du rameau)
      const perpAngle = rad + Math.PI / 2;
      const perpSpread = 5 + t * 13; // feuillage resserré autour de la branche
      const scatterPerp = (rand() - 0.5) * 2 * perpSpread;
      const scatterAlong = (rand() - 0.5) * 10;

      const x = baseX + Math.cos(perpAngle) * scatterPerp + Math.cos(rad) * scatterAlong;
      const y = baseY + Math.sin(perpAngle) * scatterPerp + Math.sin(rad) * scatterAlong;

      leaves.push({
        slot: slotIndex,
        x: Math.max(40, Math.min(960, x)),
        y: Math.max(60, Math.min(trunkTopY - 2, y)),
      });
      slotIndex++;
    }
    if (slotIndex >= TOTAL_SLOTS) break;
  }

  // Mélange déterministe : les premières feuilles débloquées se répartissent
  // sur toutes les branches et toutes les hauteurs, pas seulement près du tronc.
  let mixSeed = 12345 + themeSeed * 31;
  const mrand = () => { mixSeed = (mixSeed * 16807 + 7) % 2147483647; return (mixSeed - 1) / 2147483646; };
  for (let i = leaves.length - 1; i > 0; i--) {
    const j = Math.floor(mrand() * (i + 1));
    [leaves[i], leaves[j]] = [leaves[j], leaves[i]];
  }

  return leaves.slice(0, Math.min(visibleCount, leaves.length));
}


function Leaf({ x, y, color, mastered, delay, onClick, title, windDelay, windAmp }) {
  const size = mastered ? 10 : 8;
  // Mastered: theme color with golden ring. Not mastered: theme color, dimmer.
  const fillColor = color;
  const strokeColor = mastered ? '#f2c063' : 'rgba(255,255,255,0.2)';
  const strokeW = mastered ? 2.5 : 1;
  const opacity = mastered ? 1 : 0.7;
  // Cible tactile invisible plus large pour faciliter le tap (surtout mobile).
  const tapRadius = Math.max(size + 8, 18);

  return (
    <motion.g
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay, type: 'spring', damping: 10, stiffness: 150 }}
      style={{ cursor: 'pointer' }}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      <title>{title}</title>
      {/* Mastered glow ring (animation conservée, douce et pulsante) */}
      {mastered && (
        <circle cx={x} cy={y} r={size + 5} fill="none" stroke="rgba(255,215,0,0.25)" strokeWidth="2">
          <animate attributeName="r" values={`${size+3};${size+7};${size+3}`} dur="3s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.2;0.5;0.2" dur="3s" repeatCount="indefinite" />
        </circle>
      )}
      {/* Cible tactile invisible plus grande que la feuille visible */}
      <circle cx={x} cy={y} r={tapRadius} fill="transparent" pointerEvents="all" />
      {/* Leaf body — pas d'animation de sway, position stable */}
      <circle cx={x} cy={y} r={size} fill={fillColor} stroke={strokeColor}
        strokeWidth={strokeW} opacity={opacity} pointerEvents="none" />
      {/* Star for mastered */}
      {mastered && (
        <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle"
          fontSize="8" fill="#fff" fontWeight="bold" pointerEvents="none"
          style={{ textShadow: '0 0 3px rgba(0,0,0,0.5)' }}>★</text>
      )}
    </motion.g>
  );
}

function FloatingParticle({ delay, type }) {
  const x = 80 + Math.random() * 640;
  const dur = 10 + Math.random() * 12;
  if (type === 'firefly') {
    // Firefly: glowing yellow dot that wanders
    const wanderX = 30 + Math.random() * 40;
    return (
      <circle r={2} fill="#f2c063">
        <animate attributeName="cx" values={`${x};${x+wanderX};${x-wanderX*0.5};${x+wanderX*0.3};${x}`}
          dur={`${dur}s`} repeatCount="indefinite" begin={`${delay}s`} />
        <animate attributeName="cy" values={`${350+Math.random()*150};${200+Math.random()*100};${300+Math.random()*100};${350+Math.random()*150}`}
          dur={`${dur*1.3}s`} repeatCount="indefinite" begin={`${delay}s`} />
        <animate attributeName="opacity" values="0;0.6;0.8;0.4;0" dur={`${dur*0.7}s`} repeatCount="indefinite" begin={`${delay}s`} />
      </circle>
    );
  }
  // Regular particle: drifts upward
  return (
    <circle r={1.5 + Math.random()} fill="rgba(200,220,255,0.25)">
      <animate attributeName="cx" values={`${x};${x+(Math.random()-0.5)*80};${x}`}
        dur={`${dur}s`} repeatCount="indefinite" begin={`${delay}s`} />
      <animate attributeName="cy" values="580;-10" dur={`${dur}s`} repeatCount="indefinite" begin={`${delay}s`} />
      <animate attributeName="opacity" values="0;0.4;0.4;0" dur={`${dur}s`} repeatCount="indefinite" begin={`${delay}s`} />
    </circle>
  );
}

// ── MonoTree : arbre d'un seul thème, plein écran ──
// Rendu dans un SVG plein écran avec viewBox 1000×700.
// Les feuilles ne sont PAS cliquables — l'arbre est un trophée visuel.
// Chaque carte débloquée = une feuille. L'arbre grandit littéralement
// au fur et à mesure des débloquages. À 100%, une couronne apparaît.
function MonoTree({ branch }) {
  const colors = THEME_COLORS[branch.theme_slug] || THEME_COLORS.espace;
  const pct = branch.total_cards > 0 ? branch.unlocked_cards / branch.total_cards : 0;

  // Taille du tronc : grandit avec la progression
  const trunkHeight = 110 + Math.round(pct * 170);
  const trunkTopY = 590 - trunkHeight;
  // Largeur du tronc : grandit doucement
  const trunkW = 26 + Math.round(pct * 30);

  // 1 feuille = 1 carte débloquée (pas de plafond par palier)
  const visibleLeaves = branch.unlocked_cards;

  // Nombre de BRANCHES visibles selon la maturité : l'arbre pousse vraiment
  // (1 branche → éventail complet), il ne fait pas que changer de taille.
  // Aligné sur les paliers d'étiquette (Graine/Germe/Pousse/…).
  const unlocked = branch.unlocked_cards;
  let nBranches;
  if (unlocked === 0) nBranches = 0;          // graine : tronc nu
  else if (unlocked < 5) nBranches = 1;        // germe : 1 branche
  else if (unlocked < 15) nBranches = 2;       // pousse : 2
  else if (unlocked < 35) nBranches = 3;       // jeune arbuste : 3
  else if (pct < 0.25) nBranches = 4;          // arbuste : 4
  else if (pct < 0.50) nBranches = 5;          // jeune arbre : 5
  else if (pct < 0.75) nBranches = 6;          // arbre adulte : 6
  else nBranches = 7;                          // mature / légendaire : éventail complet
  const activeAngles = MONO_BRANCH_GROWTH_ORDER.slice(0, nBranches).map(i => MONO_BRANCH_ANGLES[i]);

  // Seed dérivé du theme_slug pour que chaque thème ait sa propre dispo stable.
  // Hash simple sur les codes des caractères.
  const themeSeed = useMemo(() => {
    let h = 0;
    for (const ch of branch.theme_slug) h = (h * 31 + ch.charCodeAt(0)) | 0;
    return Math.abs(h);
  }, [branch.theme_slug]);

  // Positions stables : les `visibleLeaves` premières d'un ordre de 200 slots
  // pré-mélangés. Ajouter une carte = ajouter une feuille au slot suivant
  // sans déplacer les précédentes.
  const leafPositions = useMemo(
    () => generateMonoTreeLeaves(visibleLeaves, trunkTopY, themeSeed, activeAngles),
    [visibleLeaves, trunkTopY, themeSeed, nBranches]
  );

  // Délai d'animation borné : à 200 feuilles, on ne peut pas faire pop chaque
  // feuille à 0.025s d'écart (ça durerait 5s). On capte un délai max global.
  const TOTAL_ANIM_MS = 1200;
  const perLeafDelay = visibleLeaves > 0
    ? Math.min(0.025, TOTAL_ANIM_MS / 1000 / visibleLeaves)
    : 0;

  const isComplete = pct >= 1.0 && branch.total_cards > 0;
  const isMature = pct >= 0.5;

  // Facteur de croissance GLOBAL de l'arbre : comme le SVG remplit toujours son
  // conteneur (viewBox fixe), faire grandir uniquement le tronc ne se voyait pas.
  // On met TOUTE la structure à l'échelle autour de la base (500,590).
  // Courbe en puissance 0.45 pour que la croissance soit déjà nette dès les
  // premiers % (4% vs 15% = différence visible), de 0.42 (graine) à 1.0 (complété).
  const growth = branch.total_cards > 0
    ? 0.42 + 0.58 * Math.pow(Math.min(1, pct), 0.45)
    : 0.42;
  const growthTransform = `translate(500 590) scale(${growth.toFixed(3)}) translate(-500 -590)`;

  return (
    <svg viewBox="0 0 1000 700" className="tree-svg" aria-label={`Arbre ${branch.theme_name}`}>
      <defs>
        <linearGradient id="monoTrunkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#8B6914" />
          <stop offset="100%" stopColor="#5D4037" />
        </linearGradient>
        <radialGradient id="monoGroundGrad" cx="50%" cy="50%">
          <stop offset="0%" stopColor="rgba(46,125,50,0.18)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <radialGradient id="monoCanopyGlow" cx="50%" cy="50%">
          <stop offset="0%" stopColor={colors.glow} />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <filter id="monoSoftGlow">
          <feGaussianBlur stdDeviation="3" />
          <feComposite in="SourceGraphic" />
        </filter>
      </defs>

      {/* Halo de fond (à partir du palier mature) — couleur du thème */}
      {isMature && (
        <circle cx="500" cy={trunkTopY - 40} r="240" fill="url(#monoCanopyGlow)" opacity={isComplete ? 0.7 : 0.4}>
          <animate attributeName="opacity"
            values={isComplete ? '0.5;0.85;0.5' : '0.3;0.55;0.3'}
            dur="5s" repeatCount="indefinite" />
        </circle>
      )}

      {/* Sol */}
      <ellipse cx="500" cy="600" rx="320" ry="40" fill="url(#monoGroundGrad)" />

      {/* Racines */}
      <path d="M460 570 Q420 590 370 600" stroke="#5D4037" strokeWidth="4" fill="none" opacity="0.5" />
      <path d="M540 570 Q580 590 630 600" stroke="#5D4037" strokeWidth="4" fill="none" opacity="0.5" />
      <path d="M500 595 Q500 620 500 640" stroke="#5D4037" strokeWidth="3" fill="none" opacity="0.4" />

      {/* Tout l'arbre (tronc + branches + feuilles + couronne) grandit ensemble */}
      <g transform={growthTransform}>
      {/* Tronc */}
      <motion.path
        key={`trunk-${branch.theme_slug}`}
        d={`M${500 - trunkW/2} 590 Q${500 - trunkW/2 - 6} ${590 - trunkHeight/2} ${500 - trunkW/2 + 3} ${trunkTopY} L${500 + trunkW/2 - 3} ${trunkTopY} Q${500 + trunkW/2 + 6} ${590 - trunkHeight/2} ${500 + trunkW/2} 590 Z`}
        fill="url(#monoTrunkGrad)"
        initial={{ scaleY: 0 }} animate={{ scaleY: 1 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: '500px 590px' }}
      />
      {/* Texture tronc */}
      <line x1="495" y1={trunkTopY + trunkHeight * 0.3} x2="490" y2={trunkTopY + trunkHeight * 0.55}
        stroke="#4E342E" strokeWidth="1" opacity="0.3" />
      <line x1="510" y1={trunkTopY + trunkHeight * 0.55} x2="513" y2={trunkTopY + trunkHeight * 0.8}
        stroke="#4E342E" strokeWidth="1" opacity="0.3" />

      {/* Branches secondaires : toujours visibles, font partie de la structure de l'arbre */}
      <g>
          {/* (la branche centrale -90 est désormais incluse dans l'éventail ci-dessous
              → elle reçoit des feuilles comme les autres) */}
          {/* Branches en éventail — 6 branches pour bien occuper la canopée
              -90° = vertical haut. Angles partis vers le haut-gauche, haut-droit
              et plus latéral pour couvrir 4 anneaux concentriques. */}
          {activeAngles.map((angleDeg, i) => {
            const rad = (angleDeg * Math.PI) / 180;
            const len = MONO_BRANCH_LEN;
            // Branches au sommet (proches de -90°) plus épaisses
            const verticalAlignement = 1 - Math.abs(angleDeg + 90) / 90;
            const thickness = 2.5 + verticalAlignement * 2;
            return (
              <motion.line key={i}
                x1="500" y1={trunkTopY}
                x2={500 + Math.cos(rad) * len}
                y2={trunkTopY + Math.sin(rad) * len}
                stroke="#8B6914" strokeWidth={thickness} strokeLinecap="round"
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                transition={{ duration: 0.8, delay: 0.8 + i * 0.08 }} />
            );
          })}
        </g>

      {/* Feuilles décoratives (1 par carte débloquée, jusqu'à 200) */}
      {leafPositions.map((pos, i) => (
        <motion.circle key={`${branch.theme_slug}-leaf-${pos.slot}`}
          cx={pos.x} cy={pos.y}
          r={7}
          fill={colors.leaf}
          stroke="rgba(255,255,255,0.15)" strokeWidth="0.8"
          opacity={0.88}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.88 }}
          transition={{
            delay: 0.8 + i * perLeafDelay,
            type: 'spring', damping: 12, stiffness: 160,
          }}
        />
      ))}

      {/* Couronne quand le thème est 100% complété */}
      {isComplete && (
        <g>
          <motion.text x="500" y={trunkTopY - 130}
            textAnchor="middle" fontSize="40"
            initial={{ y: trunkTopY - 100, opacity: 0 }}
            animate={{ y: trunkTopY - 130, opacity: 1 }}
            transition={{ delay: 2.0 }}>
            👑
          </motion.text>
          <circle cx="500" cy={trunkTopY - 50} r="10" fill="#f2c063" opacity="0.3"
            filter="url(#monoSoftGlow)">
            <animate attributeName="r" values="8;18;8" dur="3s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.2;0.55;0.2" dur="3s" repeatCount="indefinite" />
          </circle>
        </g>
      )}
      </g>

      {/* Étiquette grande, lisible, centrée en bas */}
      <text x="500" y="675"
        textAnchor="middle"
        fontSize="22" fontWeight="700"
        fill={colors.leaf}>
        {colors.icon} {branch.theme_name}
      </text>
    </svg>
  );
}


export default function Curiosaurus() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tree, setTree] = useState(null);
  const [thermos, setThermos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showIntro, setShowIntro] = useState(true);
  const [currentThemeIndex, setCurrentThemeIndex] = useState(0);
  const swipeStartX = useRef(null);
  const swipeStartY = useRef(null);

  useEffect(() => {
    Promise.all([api.getTreeState(), api.getThermometers()])
      .then(([t, th]) => { setTree(t); setThermos(th); setTimeout(() => setShowIntro(false), 1500); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tree && !loading) {
    }
  }, [tree, loading]);

  // Ordre stable des thèmes (basé sur THEME_COLORS qui définit l'ordre canonique).
  // Inclut UNIQUEMENT les thèmes accessibles au user (présents dans tree.branches).
  const orderedBranches = useMemo(() => {
    if (!tree) return [];
    const order = Object.keys(THEME_COLORS);
    const map = new Map(tree.branches.map(b => [b.theme_slug, b]));
    const ordered = [];
    for (const slug of order) {
      if (map.has(slug)) ordered.push(map.get(slug));
    }
    // Ajoute les éventuels thèmes non listés dans THEME_COLORS (fallback)
    for (const b of tree.branches) {
      if (!order.includes(b.theme_slug)) ordered.push(b);
    }
    return ordered;
  }, [tree]);

  // Clamp l'index si la liste change (ex: thème débloqué pendant la session)
  const safeIndex = Math.min(Math.max(currentThemeIndex, 0), Math.max(orderedBranches.length - 1, 0));
  const currentBranch = orderedBranches[safeIndex] || null;
  const currentStage = currentBranch
    ? getMaturityStage(currentBranch.unlocked_cards, currentBranch.total_cards)
    : { id: 'dormant', label: '', emoji: '' };
  // Niveau PAR THÈME (1→10), avance tous les 10% du thème → un vrai numéro qui bouge.
  const currentLevel = currentBranch && currentBranch.total_cards > 0
    ? Math.min(10, Math.floor(currentBranch.unlocked_cards / currentBranch.total_cards * 10) + 1)
    : 1;

  const hasPrev = safeIndex > 0;
  const hasNext = safeIndex < orderedBranches.length - 1;

  const goPrev = () => { if (hasPrev) setCurrentThemeIndex(safeIndex - 1); };
  const goNext = () => { if (hasNext) setCurrentThemeIndex(safeIndex + 1); };

  // Swipe horizontal : seuil 50px, et le geste doit être plus horizontal que vertical
  // (sinon on n'interfère pas avec le scroll de la page).
  const onTreeTouchStart = (e) => {
    if (e.touches && e.touches.length === 1) {
      swipeStartX.current = e.touches[0].clientX;
      swipeStartY.current = e.touches[0].clientY;
    }
  };
  const onTreeTouchEnd = (e) => {
    if (swipeStartX.current == null || swipeStartY.current == null) return;
    const endTouch = e.changedTouches && e.changedTouches[0];
    if (!endTouch) return;
    const dx = endTouch.clientX - swipeStartX.current;
    const dy = endTouch.clientY - swipeStartY.current;
    swipeStartX.current = null;
    swipeStartY.current = null;
    if (Math.abs(dx) < 50) return;
    if (Math.abs(dy) > Math.abs(dx)) return; // mouvement plutôt vertical, ignorer
    if (dx < 0) goNext();
    else goPrev();
  };

  if (loading) {
    return <div className="loading-screen"><div className="loading-rocket">🌳</div><p>Ton Curiosaurus pousse...</p></div>;
  }
  if (!tree) {
    return <div className="loading-screen"><p>Erreur de chargement</p>
      <button className="btn btn-primary" onClick={() => navigate('/')}>Retour</button></div>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <NavBar />

      <div className="container">
        <button className="back-btn" onClick={() => navigate('/')}>← Retour</button>

        <div className="tree-header">
          <motion.div className="tree-level"
            key={`lvl-${currentBranch?.theme_slug}`}
            initial={{ x: -30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }}>
            <span className="tree-level-emoji">{currentStage.emoji || '🌱'}</span>
            <div>
              <div className="tree-level-name">Niveau {currentLevel} · {currentStage.label || 'Graine'}</div>
              <div className="tree-level-detail">{currentBranch?.theme_name || ''}</div>
            </div>
          </motion.div>
          <motion.div className="tree-stats-row"
            key={`hdr-stats-${currentBranch?.theme_slug}`}
            initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.4 }}>
            <div className="tree-stat">
              <span className="tree-stat-value">{currentBranch?.unlocked_cards ?? 0}</span>
              <span className="tree-stat-label">Feuilles</span>
            </div>
            <div className="tree-stat">
              <span className="tree-stat-value">{currentBranch?.mastered_cards ?? 0}</span>
              <span className="tree-stat-label">Maîtrisées ★</span>
            </div>
            <div className="tree-stat">
              <span className="tree-stat-value">{currentBranch?.total_cards ?? 0}</span>
              <span className="tree-stat-label">Total</span>
            </div>
          </motion.div>
        </div>

        {/* Picker horizontal de thèmes (chips) */}
        <motion.div className="tree-theme-picker"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}>
          {orderedBranches.map((br, idx) => {
            const c = THEME_COLORS[br.theme_slug] || THEME_COLORS.espace;
            const active = idx === currentThemeIndex;
            const pct = br.total_cards > 0 ? Math.round(br.unlocked_cards / br.total_cards * 100) : 0;
            return (
              <button key={br.theme_slug}
                className={`tree-chip ${active ? 'tree-chip-active' : ''}`}
                onClick={() => setCurrentThemeIndex(idx)}
                style={active ? { borderColor: c.leaf, color: c.leaf } : undefined}
                aria-label={`Voir l'arbre ${br.theme_name} (${pct}%)`}>
                <span className="tree-chip-icon">{c.icon}</span>
                <span className="tree-chip-pct">{pct}%</span>
              </button>
            );
          })}
        </motion.div>

        {/* Lien vers l'encyclopédie filtré sur le thème courant */}
        <motion.button
          className="tree-cards-link"
          onClick={() => navigate(`/cards?theme=${currentBranch?.theme_slug || ''}`)}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          aria-label="Voir les fiches débloquées de ce thème"
        >
          📚 Voir mes fiches {currentBranch ? currentBranch.theme_name.split(' ')[0] : ''} →
        </motion.button>

        {/* Arbre mono-thème avec flèches et swipe */}
        <motion.div className="tree-container mono"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          onTouchStart={onTreeTouchStart}
          onTouchEnd={onTreeTouchEnd}>

          {/* Flèche gauche */}
          <button className="tree-arrow tree-arrow-left"
            onClick={goPrev}
            disabled={!hasPrev}
            aria-label="Thème précédent">
            ‹
          </button>

          {/* L'arbre du thème courant. key force le re-rendu avec animations à chaque changement. */}
          <AnimatePresence mode="wait">
            {currentBranch && (
              <motion.div
                key={currentBranch.theme_slug}
                className="mono-tree-wrap"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}>
                <MonoTree branch={currentBranch} />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Flèche droite */}
          <button className="tree-arrow tree-arrow-right"
            onClick={goNext}
            disabled={!hasNext}
            aria-label="Thème suivant">
            ›
          </button>
        </motion.div>

        {/* (compteurs + palier sont désormais dans le header, par thème) */}

        {/* (légende « Bonne réponse / À retravailler » retirée — demande de Jérémy) */}

        {/* Legend */}
        <div className="tree-legend">
          {tree.branches.map(branch => {
            const colors = THEME_COLORS[branch.theme_slug] || THEME_COLORS.espace;
            const pct = branch.total_cards > 0 ? Math.round(branch.unlocked_cards / branch.total_cards * 100) : 0;
            return (
              <motion.div key={branch.theme_id} className="tree-legend-item"
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}>
                <span className="tree-legend-dot" style={{ background: colors.leaf }} />
                <span className="tree-legend-name">{colors.icon} {branch.theme_name}</span>
                <span className="tree-legend-progress">{branch.unlocked_cards}/{branch.total_cards} ({pct}%)</span>
              </motion.div>
            );
          })}
        </div>

        {/* Coach analysis */}
        {tree.total_leaves > 0 && (() => {
          const insights = analyzeTree(tree);
          if (!insights || insights.length === 0) return null;
          return (
            <motion.div className="tree-coach"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2 }}>
              <div className="tree-coach-header">
                <span className="tree-coach-avatar">🦎</span>
                <span className="tree-coach-title">Curiosaurus dit :</span>
              </div>
              <div className="tree-coach-messages">
                {insights.map((msg, i) => (
                  <motion.div key={i}
                    className={`tree-coach-msg ${msg.type}`}
                    initial={{ opacity: 0, x: -15 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 2.2 + i * 0.3 }}>
                    <span className="tree-coach-msg-icon">{msg.icon}</span>
                    <span className="tree-coach-msg-text">{msg.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          );
        })()}

        {tree.total_leaves === 0 && (
          <div className="cards-empty" style={{ marginTop: 20 }}>
            <div className="emoji">🌱</div>
            <h3>Ton Curiosaurus est encore une graine !</h3>
            <p style={{ marginTop: 8 }}>Réponds à des quiz pour faire pousser ton arbre</p>
            <button className="btn btn-primary" style={{ maxWidth: 300, margin: '24px auto 0' }}
              onClick={() => navigate('/')}>🚀 Commencer un quiz</button>
          </div>
        )}
      </div>
    </motion.div>
  );
}
